import { useTranslation } from '@multaqa/i18n'
import { AppError, useAppErrorMessages } from '@multaqa/ui/components/async/app-error'
import { FormSkeleton } from '@multaqa/ui/components/async/form-skeleton'
import { EmptyState } from '@multaqa/ui/components/ui/empty-state'
import type { TemplateItemInput } from './api'
import {
  useCreateTemplateDepartment,
  useCreateTemplatePosition,
  useDeleteTemplateDepartment,
  useDeleteTemplatePosition,
  useGroupDefaults,
  useTemplateDepartments,
  useTemplatePositions,
  useUpdateTemplateDepartment,
  useUpdateTemplatePosition,
} from './api'
import { SettingsCard } from './group-defaults-settings'
import type { TemplateActions } from './group-defaults-templates'
import { TemplateTable } from './group-defaults-templates'

export const SYSTEM_DEPARTMENT_DEFAULTS: readonly TemplateItemInput[] = [
  { name: { en: 'Management', ar: 'الإدارة' }, code: 'MANAGEMENT' },
  { name: { en: 'Finance', ar: 'المالية' }, code: 'FINANCE' },
  { name: { en: 'Human Resources', ar: 'الموارد البشرية' }, code: 'HUMAN_RESOURCES' },
  { name: { en: 'Sales', ar: 'المبيعات' }, code: 'SALES' },
  { name: { en: 'Operations', ar: 'العمليات' }, code: 'OPERATIONS' },
]
export const SYSTEM_POSITION_DEFAULTS: readonly TemplateItemInput[] = [
  { name: { en: 'General Manager', ar: 'المدير العام' }, code: 'GENERAL_MANAGER' },
  { name: { en: 'Finance Manager', ar: 'مدير المالية' }, code: 'FINANCE_MANAGER' },
  { name: { en: 'HR Manager', ar: 'مدير الموارد البشرية' }, code: 'HR_MANAGER' },
  { name: { en: 'Sales Manager', ar: 'مدير المبيعات' }, code: 'SALES_MANAGER' },
  { name: { en: 'Operations Manager', ar: 'مدير العمليات' }, code: 'OPERATIONS_MGR' },
]

export function GroupDefaults({ groupId }: { groupId: string }) {
  const { t } = useTranslation()
  const messages = useAppErrorMessages()
  const defaults = useGroupDefaults(groupId)
  const departments = useTemplateDepartments(groupId)
  const positions = useTemplatePositions(groupId)
  const createDepartment = useCreateTemplateDepartment()
  const updateDepartment = useUpdateTemplateDepartment()
  const deleteDepartment = useDeleteTemplateDepartment()
  const createPosition = useCreateTemplatePosition()
  const updatePosition = useUpdateTemplatePosition()
  const deletePosition = useDeleteTemplatePosition()

  if (defaults.isLoading)
    return (
      <div data-testid="group-defaults-loading">
        <FormSkeleton fields={8} />
      </div>
    )
  if (defaults.isError)
    return <AppError error={defaults.error} messages={messages} retry={defaults.refetch} />
  if (!defaults.data)
    return (
      <EmptyState
        title={t('groups.defaults.empty.title')}
        description={t('groups.defaults.empty.body')}
      />
    )

  const departmentActions: TemplateActions = {
    pending: createDepartment.isPending || updateDepartment.isPending || deleteDepartment.isPending,
    create: (input) => createDepartment.mutateAsync({ groupId, input }),
    update: (id, input) =>
      updateDepartment.mutateAsync({ groupId, templateDepartmentId: id, input }),
    remove: (id) => deleteDepartment.mutateAsync({ groupId, templateDepartmentId: id }),
  }
  const positionActions: TemplateActions = {
    pending: createPosition.isPending || updatePosition.isPending || deletePosition.isPending,
    create: (input) => createPosition.mutateAsync({ groupId, input }),
    update: (id, input) => updatePosition.mutateAsync({ groupId, templatePositionId: id, input }),
    remove: (id) => deletePosition.mutateAsync({ groupId, templatePositionId: id }),
  }
  return (
    <div className="grid gap-4" data-testid="group-defaults">
      <p className="border-s-2 border-ring bg-muted/40 p-3 text-body" role="note">
        {t('groups.defaults.note')}
      </p>
      <SettingsCard groupId={groupId} data={defaults.data} />
      <TemplateTable
        kind="departments"
        items={departments.data ?? []}
        loading={departments.isLoading}
        error={departments.error}
        retry={departments.refetch}
        actions={departmentActions}
        systemDefaults={SYSTEM_DEPARTMENT_DEFAULTS}
      />
      <TemplateTable
        kind="positions"
        items={positions.data ?? []}
        loading={positions.isLoading}
        error={positions.error}
        retry={positions.refetch}
        actions={positionActions}
        systemDefaults={SYSTEM_POSITION_DEFAULTS}
      />
    </div>
  )
}
